import React, { useState, useEffect, useRef, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import AuthContext from '../../context/AuthContext';
import {
  Card,
  Button,
  Row,
  Col,
  Alert,
  Spinner,
  Table,
  Modal,
  Badge,
  Pagination
} from 'react-bootstrap';
import {
  FaPrint,
  FaArrowLeft,
  FaCalendarAlt,
  FaEdit,
  FaTrash,
  FaXRay,
  FaCheck,
  FaUserEdit,
  FaDownload
} from 'react-icons/fa';

// Add custom print styles
const PrintStyles = () => (
  <style>
    {`
      @import url('https://fonts.googleapis.com/css2?family=Noto+Nastaliq+Urdu:wght@400;700&display=swap');
      
      /* Custom medication pagination styles */
      .medication-pagination {
        margin: 15px 0;
        padding: 8px;
        background-color: #f8f9fa;
        border-radius: 5px;
        box-shadow: 0 1px 3px rgba(0,0,0,0.1);
      }
      
      .medication-pagination .page-count {
        color: #6c757d;
        font-weight: 500;
      }
      
      .medication-pagination .pagination {
        margin-bottom: 0;
      }
      
      @media print {
        /* Hide everything first */
        body * {
          visibility: hidden !important;
        }
        
        /* Show only report card and its children */
        .report-card, .report-card * {
          visibility: visible !important;
        }
        
        /* Position report card */
        .report-card {
          position: absolute !important;
          left: 0 !important;
          top: 0 !important;
          width: 100% !important;
          padding: 10px !important;
          min-height: 97vh !important;
        }
        
        /* Hide all no-print elements */
        .no-print, .no-print * {
          display: none !important;
          visibility: hidden !important;
        }
        
        /* Hide all buttons and interactive elements */
        .btn, button, .btn-group, .pagination {
          display: none !important;
          visibility: hidden !important;
        }
        
        /* Hide medication pagination */
        .medication-pagination {
          display: none !important;
          visibility: hidden !important;
        }
        
        /* Hide cards that are not part of report */
        .card:not(.report-card) {
          display: none !important;
          visibility: hidden !important;
        }
        
        /* Hide badges */
        .badge {
          display: none !important;
          visibility: hidden !important;
        }
        
        /* Hide alerts */
        .alert {
          display: none !important;
          visibility: hidden !important;
        }
        
        /* Clinic header styles - Important! Always apply these */
        .clinic-header {
          display: flex !important;
          align-items: center !important;
          border-bottom: 2px solid #333 !important;
          padding-bottom: 10px !important;
          margin-bottom: 15px !important;
          text-align: left !important;
        }
        
        .clinic-header img {
          max-width: 80px !important;
          height: auto !important;
          margin-right: 15px !important;
        }
        
        .clinic-header h2 {
          margin: 0 !important;
          font-family: Georgia, serif !important;
          color: #1a5276 !important;
          font-weight: bold !important;
          letter-spacing: 1px !important;
        }
        
        .medication-table th, .medication-table td {
          padding: 2px;
          font-size: 10px;
          border: 1px solid #ddd;
        }
        .medication-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 5px;
        }
        .report-header p, .report-body p {
          margin-bottom: 2px;
          font-size: 11px;
        }
        h5 {
          font-size: 12px;
          margin-bottom: 3px;
          margin-top: 3px;
        }
        .doctor-signature {
          margin-top: 20px;
          border-top: 1px solid #000;
          text-align: center;
          width: 120px;
          display: inline-block;
          margin-right: 15px;
          margin-left: 15px;
        }
        .doctor-name, .doctor-qualification {
          margin: 0;
          font-size: 11px;
        }
        .doctor-name {
          font-weight: bold;
        }
        .footer {
          margin-top: 10px !important;
          position: relative;
          bottom: 0;
        }
        @page {
          size: A4;
          margin: 15mm 10mm 20mm 10mm;
          @bottom-right {
            content: "Page " counter(page) " of " counter(pages);
            font-size: 10px;
            color: #666;
          }
        }
        .bi-lingual {
          display: flex;
          justify-content: space-between;
        }
        .urdu-text {
          font-family: 'Noto Nastaliq Urdu', serif;
          font-size: 11px;
          text-align: right;
          direction: rtl;
          color: #333;
          margin-top: 2px;
          font-weight: 500;
        }
        hr {
          margin: 3px 0;
        }
        .patient-details {
          margin-bottom: 5px !important;
        }
        .mt-4 {
          margin-top: 0.5rem !important;
        }
        .mt-5 {
          margin-top: 1rem !important;
        }
        .mb-4 {
          margin-bottom: 0.5rem !important;
        }
        .mb-3 {
          margin-bottom: 0.3rem !important;
        }
        
        /* Ultrasound report styles - SEPARATE PAGE */
        .ultrasound-report-page {
          page-break-before: always !important;
          page-break-after: auto !important;
          min-height: 100vh !important;
        }
        .ultrasound-report h2 {
          font-size: 18px;
          text-align: center;
          margin-bottom: 10px;
          font-weight: bold;
        }
        .ultrasound-report h3 {
          font-size: 14px;
          margin-top: 10px;
          margin-bottom: 5px;
          font-weight: bold;
        }
        .ultrasound-report .field-label {
          font-weight: bold;
          margin-right: 5px;
        }
        .ultrasound-report .field-value {
          margin-bottom: 5px;
        }
        .ultrasound-report-body {
          margin-top: 10px;
        }
        .ultrasound-tables table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 10px;
        }
        .ultrasound-tables th, .ultrasound-tables td {
          border: 1px solid #ddd;
          padding: 4px;
          font-size: 10px;
        }
        .ultrasound-tables th {
          background-color: #f8f9fa;
          font-weight: bold;
        }
        
        /* Ultrasound images styles */
        .ultrasound-images {
          margin: 15px 0 !important;
          page-break-inside: avoid !important;
        }
        .ultrasound-image-container {
          margin-bottom: 15px !important;
          text-align: center !important;
          page-break-inside: avoid !important;
        }
        .ultrasound-image {
          max-width: 100% !important;
          border: 1px solid #ddd !important;
          background-color: #f8f9fa !important;
          padding: 4px !important;
        }
        .ultrasound-image-caption {
          font-style: italic !important;
          font-size: 10px !important;
          text-align: center !important;
          margin-top: 5px !important;
          color: #666 !important;
        }
        
        /* Rules for ultrasound report images */
        .ultrasound-report-image {
          /* Use inline styles for dimensions */
          max-width: 100% !important;
          object-fit: contain !important;
          border: 1px solid #ddd !important;
          padding: 4px !important;
          background-color: #f8f9fa !important;
        }
        
        /* Force horizontally paired images with stricter rules */
        .ultrasound-images table {
          width: 100% !important;
          table-layout: fixed !important;
          border-collapse: collapse !important;
        }
        .ultrasound-images tr {
          display: flex !important;
          flex-direction: row !important;
          width: 100% !important;
        }
        .ultrasound-images td {
          display: inline-block !important;
          width: 50% !important;
          max-width: 50% !important;
          float: left !important;
          box-sizing: border-box !important;
          padding: 5px !important;
          text-align: center !important;
        }
        /* Preserve custom dimensions */
        .ultrasound-images img {
          /* Use inline attributes for width and height */
          max-width: 100% !important;
          object-fit: contain !important;
          border: 1px solid #ddd !important;
          background-color: #f8f9fa !important;
          padding: 4px !important;
          display: inline-block !important;
          margin: 0 auto !important;
        }
        
        /* Additional fixes for Bootstrap Row/Col */
        .ultrasound-images .row {
          display: flex !important;
          flex-wrap: wrap !important;
          width: 100% !important;
        }
        .ultrasound-images [class*='col-'] {
          width: 50% !important;
          max-width: 50% !important;
          flex: 0 0 50% !important;
          float: left !important;
          display: inline-block !important;
          box-sizing: border-box !important;
          padding: 5px !important;
        }
        
        /* Footer styles */
        .report-footer {
          position: fixed !important;
          bottom: 0 !important;
          left: 0 !important;
          right: 0 !important;
          padding: 8px 20px !important;
          background: white !important;
          border-top: 1px solid #ddd !important;
          width: 100% !important;
          box-sizing: border-box !important;
        }
        
        .footer-doctor-signature {
          display: flex !important;
          justify-content: space-around !important;
          margin-bottom: 8px !important;
          padding-top: 2px !important;
          width: 100% !important;
        }
        
        .footer-doctor-signature > div {
          width: 33.33% !important;
          text-align: center !important;
          border-top: 1px solid #000 !important;
          padding-top: 2px !important;
          margin: 0 5px !important;
        }
        
        .footer-doctor-name {
          font-size: 10px !important;
          font-weight: bold !important;
          margin: 0 !important;
        }
        
        .footer-doctor-qualification {
          font-size: 9px !important;
          margin: 0 !important;
          color: #666 !important;
        }
        
        .footer-clinic-hours {
          display: block !important;
          font-size: 8px !important;
          color: #666 !important;
          text-align: center !important;
          margin: 0 0 3px 0 !important;
        }
        
        .footer-credit {
          display: block !important;
          font-size: 8px !important;
          color: #666 !important;
          text-align: center !important;
          margin: 0 !important;
        }
        
        /* Clinic hours in footer */
        .clinic-hours {
          text-align: center !important;
          margin: 5px 0 !important;
          font-size: 9px !important;
          color: #555 !important;
        }
        
        /* Move doctor signatures into footer */
        .doctor-signatures {
          display: flex !important;
          justify-content: center !important;
          flex-wrap: wrap !important;
          margin-bottom: 15px !important;
        }
        
        .doctor-signature {
          text-align: center !important;
          width: 100px !important;
          display: inline-block !important;
          margin-right: 10px !important;
          margin-left: 10px !important;
        }
        
        .doctor-signature p {
          margin-bottom: 0 !important;
          font-size: 10px !important;
        }
        
        .doctor-name {
          font-weight: bold !important;
        }
        
        /* Report content with proper spacing */
        .report-content {
          padding-bottom: 30mm !important;
        }
      }
    `}
  </style>
);

const ReportDetail = () => {
  const { id } = useParams();
  const reportRef = useRef();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  // Check if this is public access (no user logged in)
  const isPublicAccess = !user;
  
  // URL Protection for public access
  useEffect(() => {
    if (isPublicAccess) {
      // Disable back button
      window.history.pushState(null, '', window.location.href);
      const handlePopState = () => {
        window.history.pushState(null, '', window.location.href);
      };
      window.addEventListener('popstate', handlePopState);
      
      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [isPublicAccess]);
  
  // Check if hideBack parameter is in URL
  const searchParams = new URLSearchParams(window.location.search);
  const hideBack = searchParams.get('hideBack') === 'true';
  
  const [report, setReport] = useState(null);
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [signedDoctors, setSignedDoctors] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [medicationsPerPage] = useState(10);
  
  // Helper function to convert time to 12-hour format with AM/PM
  const formatTimeWithAMPM = (time) => {
    if (!time) return '';
    
    // Parse the 24-hour time format
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    
    // Convert to 12-hour format
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12; // Convert 0 to 12 for 12 AM
    
    return `${displayHour}:${minutes} ${period}`;
  };
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch report
        console.log("Fetching report with ID:", id);
        const reportRes = await axios.get(`/api/reports.php?id=${id}`);
        console.log("Raw API response:", reportRes.data);
        
        if (!reportRes.data) {
          setError('Report data not found');
          setLoading(false);
          return;
        }
        
        const reportData = {...reportRes.data};
        
        // Fetch all medicines to get names for medications
        let allMedicines = [];
        try {
          const medicinesRes = await axios.get('/api/medicines.php');
          allMedicines = medicinesRes.data;
          console.log("All medicines:", allMedicines);
        } catch (err) {
          console.error("Error fetching medicines:", err);
        }
        
        // Process medications to add medicine names
        if (reportData.medications && reportData.medications.length > 0) {
          console.log("Original medications:", JSON.stringify(reportData.medications));
          
          reportData.medications = reportData.medications.map(med => {
            // If medicine name is already available in some form, use it
            if (med.medicineName) {
              console.log("Medicine already has medicineName:", med.medicineName);
              return med;
            }
            
            // Check medicine_data (used by server to store names)
            if (med.medicine_data && med.medicine_data.name) {
              console.log("Medicine has name in medicine_data:", med.medicine_data.name);
              return {
                ...med,
                medicineName: med.medicine_data.name
              };
            }
            
            // If medicine is already an object with name, use it directly
            if (med.medicine && typeof med.medicine === 'object' && med.medicine.name) {
              console.log("Medicine already has name object:", med.medicine.name);
              return {
                ...med,
                medicineName: med.medicine.name,
                medicineDetails: med.medicine
              };
            }
            
            // Get ID from medicine field (handle both string ID or object with ID)
            const medicineId = (typeof med.medicine === 'string') 
              ? med.medicine 
              : (med.medicine && med.medicine.id) 
                ? med.medicine.id 
                : (med.medicine && med.medicine._id) 
                  ? med.medicine._id 
                  : null;
                  
            console.log("Looking up medicine with ID:", medicineId);
            
            if (medicineId) {
              // Try different ways to match the medicine
              const medicineDetails = allMedicines.find(m => {
                // Try several matching methods
                return (m.id && m.id.toString() === medicineId.toString()) || 
                       (m._id && m._id.toString() === medicineId.toString()) ||
                       (m.id && medicineId.toString().includes(m.id.toString())) ||
                       (m._id && medicineId.toString().includes(m._id.toString()));
              });
              
              if (medicineDetails) {
                console.log(`Found medicine for ID ${medicineId}:`, medicineDetails.name);
                return {
                  ...med,
                  medicineName: medicineDetails.name,
                  medicineDetails: medicineDetails
                };
              } else {
                console.warn(`No matching medicine found for ID: ${medicineId}`);
              }
            }
            
            return med;
          });
          
          console.log("Enhanced medications:", JSON.stringify(reportData.medications));
        }
        
        setReport(reportData);
        
        // Fetch clinic info
        const clinicRes = await axios.get('/api/clinic.php');
        setClinic(clinicRes.data);
        console.log("Clinic data:", clinicRes.data);
        
        let doctorsData = [];
        
        // Fetch signed doctors if available in the report
        if (reportRes.data.signedByDoctors && reportRes.data.signedByDoctors.length > 0) {
          console.log("Using report signed doctors:", reportRes.data.signedByDoctors);
          for (const doctorId of reportRes.data.signedByDoctors) {
            try {
              const docRes = await axios.get(`/api/doctors.php?id=${doctorId}`);
              doctorsData.push(docRes.data);
            } catch (error) {
              console.error("Error fetching doctor:", error);
            }
          }
        } 
        // If no signed doctors in report, use clinic doctors
        else if (clinicRes.data && clinicRes.data.doctors) {
          console.log("Using clinic doctors. Original clinic doctors data:", clinicRes.data.doctors);
          
          let doctorIds = [];
          
          // Handle different formats of the clinic.doctors data
          if (Array.isArray(clinicRes.data.doctors)) {
            doctorIds = clinicRes.data.doctors
              .filter(doc => doc !== null && doc !== undefined)
              .map(doc => {
                if (typeof doc === 'object' && doc) {
                  return doc._id || doc.id;
                }
                return doc;
              });
          } else if (typeof clinicRes.data.doctors === 'object' && clinicRes.data.doctors !== null) {
            // Handle if it's a Sequelize result
            if (Array.isArray(clinicRes.data.doctors.Doctors)) {
              doctorIds = clinicRes.data.doctors.Doctors
                .filter(doc => doc !== null && doc !== undefined)
                .map(doc => doc.id || doc._id);
            }
          }
          
          console.log("Extracted doctor IDs:", doctorIds);
          
          // Fetch each doctor's details
          for (const doctorId of doctorIds) {
            try {
              if (doctorId) {
                // Make sure we convert to string for the API call if needed
                const docRes = await axios.get(`/api/doctors.php?id=${doctorId}`);
                doctorsData.push(docRes.data);
                console.log("Fetched clinic doctor:", docRes.data.name);
              }
            } catch (error) {
              console.error(`Error fetching clinic doctor with ID ${doctorId}:`, error);
            }
          }
        }
        
        console.log("Final doctors to show:", doctorsData);
        setSignedDoctors(doctorsData);
        
        setLoading(false);
      } catch (err) {
        console.error("Error in fetchData:", err);
        if (err.response) {
          console.error("Response data:", err.response.data);
          console.error("Response status:", err.response.status);
          setError(`Failed to load report data: ${err.response.data.msg || err.message}`);
        } else if (err.request) {
          console.error("No response received:", err.request);
          setError('No response from server. Network issue or server down.');
        } else {
          console.error("Request setup error:", err.message);
          setError(`Request setup error: ${err.message}`);
        }
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id]);
  
  const handlePrint = () => {
    const patientName = report && (report.patient || report.Patient) ? (report.patient || report.Patient).name : 'Patient';
    document.title = `${patientName} - Medical Report`;
    window.print();
    document.title = "Clinic Management";
  };
  
  const handleDelete = async () => {
    try {
      await axios.delete(`/api/reports.php?id=${id}`);
      navigate('/reports');
    } catch (err) {
      console.error(err);
      setError('Failed to delete report');
    }
  };
  
  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading report data...</p>
      </div>
    );
  }
  
  // Check for both uppercase and lowercase properties for backward compatibility with more robust null checks
  const patientData = report && (report.patient || report.Patient) ? (report.patient || report.Patient) : null;
  const doctorData = report && (report.doctor || report.Doctor) ? (report.doctor || report.Doctor) : null;
  
  // Create safe default objects if data is missing
  const safePatientData = patientData || { 
    patientId: "Unknown",
    name: "Unknown Patient", 
    age: "?", 
    gender: "?", 
    bloodGroup: "Unknown" 
  };
  
  const safeDoctorData = doctorData || { 
    name: "Unknown Doctor" 
  };
  
  if (error || !report) {
    return (
      <Alert variant="danger">
        {error || 'Report data not found'}. <Link to="/reports">Go back to reports</Link>
      </Alert>
    );
  }
  
  const formattedDate = new Date(report.date).toLocaleDateString();
  const formattedTime = new Date(report.date).toLocaleTimeString();
  
  // Function to get the Urdu equivalent for medication timings
  const getUrduTiming = (timing, dosage) => {
    try {
    const timingMap = {
      morning: "صبح",
      afternoon: "دوپہر",
      evening: "شام",
      night: "رات"
    };
    
    const beforeAfterMap = {
      before: "کھانے سے پہلے",
      after: "کھانے کے بعد",
      both: "کھانے سے پہلے/بعد",
      none: ""
    };
      
      if (!dosage) {
        return timingMap[timing] || "";
      }
    
    let before_after = 'none';
    
    if (timing === 'morning') {
      if (dosage.morningBeforeFood && dosage.morningAfterFood) before_after = 'both';
      else if (dosage.morningBeforeFood) before_after = 'before';
      else if (dosage.morningAfterFood) before_after = 'after';
    } else if (timing === 'afternoon') {
      if (dosage.afternoonBeforeFood && dosage.afternoonAfterFood) before_after = 'both';
      else if (dosage.afternoonBeforeFood) before_after = 'before';
      else if (dosage.afternoonAfterFood) before_after = 'after';
    } else if (timing === 'evening') {
      if (dosage.eveningBeforeFood && dosage.eveningAfterFood) before_after = 'both';
      else if (dosage.eveningBeforeFood) before_after = 'before';
      else if (dosage.eveningAfterFood) before_after = 'after';
    } else if (timing === 'night') {
      if (dosage.nightBeforeFood && dosage.nightAfterFood) before_after = 'both';
      else if (dosage.nightBeforeFood) before_after = 'before';
      else if (dosage.nightAfterFood) before_after = 'after';
    }
    
    // Backward compatibility
    if (before_after === 'none' && (dosage.beforeFood || dosage.afterFood)) {
      if (dosage.beforeFood && dosage.afterFood) before_after = 'both';
      else if (dosage.beforeFood) before_after = 'before';
      else if (dosage.afterFood) before_after = 'after';
    }
    
      return `${timingMap[timing] || ""} ${beforeAfterMap[before_after] || ""}`.trim();
    } catch (error) {
      console.error("Error getting Urdu timing:", error);
      return "";
    }
  };
  
  // Creates a readable format for medication dosage
  const formatMedicationInstructions = (med) => {
    try {
    const timings = [];
    const { dosage } = med;
      
      if (!dosage) {
        return "No dosage information";
      }
    
    if (dosage.morning) {
      let ba = "";
      if (dosage.morningBeforeFood && dosage.morningAfterFood) ba = "before & after food";
      else if (dosage.morningBeforeFood) ba = "before food";
      else if (dosage.morningAfterFood) ba = "after food";
      else if (dosage.beforeFood && dosage.afterFood) ba = "before & after food"; // Backward compatibility
      else if (dosage.beforeFood) ba = "before food"; // Backward compatibility
      else if (dosage.afterFood) ba = "after food"; // Backward compatibility
      
      timings.push(`Morning ${ba}`);
    }
    
    if (dosage.afternoon) {
      let ba = "";
      if (dosage.afternoonBeforeFood && dosage.afternoonAfterFood) ba = "before & after food";
      else if (dosage.afternoonBeforeFood) ba = "before food";
      else if (dosage.afternoonAfterFood) ba = "after food";
      else if (dosage.beforeFood && dosage.afterFood) ba = "before & after food"; // Backward compatibility
      else if (dosage.beforeFood) ba = "before food"; // Backward compatibility
      else if (dosage.afterFood) ba = "after food"; // Backward compatibility
      
      timings.push(`Afternoon ${ba}`);
    }
    
    if (dosage.evening) {
      let ba = "";
      if (dosage.eveningBeforeFood && dosage.eveningAfterFood) ba = "before & after food";
      else if (dosage.eveningBeforeFood) ba = "before food";
      else if (dosage.eveningAfterFood) ba = "after food";
      else if (dosage.beforeFood && dosage.afterFood) ba = "before & after food"; // Backward compatibility
      else if (dosage.beforeFood) ba = "before food"; // Backward compatibility
      else if (dosage.afterFood) ba = "after food"; // Backward compatibility
      
      timings.push(`Evening ${ba}`);
    }
    
    if (dosage.night) {
      let ba = "";
      if (dosage.nightBeforeFood && dosage.nightAfterFood) ba = "before & after food";
      else if (dosage.nightBeforeFood) ba = "before food";
      else if (dosage.nightAfterFood) ba = "after food";
      else if (dosage.beforeFood && dosage.afterFood) ba = "before & after food"; // Backward compatibility
      else if (dosage.beforeFood) ba = "before food"; // Backward compatibility
      else if (dosage.afterFood) ba = "after food"; // Backward compatibility
      
      timings.push(`Night ${ba}`);
    }
    
      return timings.join(", ") || "No specific timing";
    } catch (error) {
      console.error("Error formatting medication instructions:", error);
      return "Dosage information unavailable";
    }
  };
  
  // Creates a readable format for medication dosage in Urdu
  const formatMedicationInstructionsUrdu = (med) => {
    try {
    const timings = [];
    const { dosage } = med;
      
      if (!dosage) {
        return "";
      }
    
    if (dosage.morning) timings.push(getUrduTiming("morning", dosage));
    if (dosage.afternoon) timings.push(getUrduTiming("afternoon", dosage));
    if (dosage.evening) timings.push(getUrduTiming("evening", dosage));
    if (dosage.night) timings.push(getUrduTiming("night", dosage));
    
      return timings.join("، ") || "";
    } catch (error) {
      console.error("Error formatting Urdu medication instructions:", error);
      return "";
    }
  };
  
  // Add this function to render the ultrasound report based on its type
  const renderUltrasoundReport = (report) => {
    try {
      const { ultrasoundReport } = report || {};
      
      if (!ultrasoundReport || !ultrasoundReport.hasUltrasound) {
        return null;
      }
      
      // Function to render measurements for obstetric ultrasound
      const renderObstetricMeasurements = () => {
        try {
          // Filter out rows with N/A values
          const hasBiometryData = ultrasoundReport.biparietalDiameter || 
                                ultrasoundReport.headCircumference || 
                                ultrasoundReport.abdominalCircumference || 
                                ultrasoundReport.femurLength;
          
          return (
            <div className="ultrasound-tables">
              <h3>Fetal Biometry</h3>
              <Table bordered size="sm">
                <thead>
                  <tr>
                    <th>Parameter</th>
                    <th>Measurement</th>
                  </tr>
                </thead>
                <tbody>
                  {ultrasoundReport.gestationalAge && (
                    <tr>
                      <th>Gestational Age</th>
                      <td>{ultrasoundReport.gestationalAge}</td>
                    </tr>
                  )}
                  {ultrasoundReport.fetalHeartRate && (
                    <tr>
                      <th>Fetal Heart Rate</th>
                      <td>{ultrasoundReport.fetalHeartRate} bpm</td>
                    </tr>
                  )}
                  {ultrasoundReport.gestationalSac && (
                    <tr>
                      <th>Gestational Sac (GS)</th>
                      <td>{ultrasoundReport.gestationalSac} mm</td>
                    </tr>
                  )}
                  {ultrasoundReport.crownRumpLength && (
                    <tr>
                      <th>Crown-Rump Length (CRL)</th>
                      <td>{ultrasoundReport.crownRumpLength} mm</td>
                    </tr>
                  )}
                  {ultrasoundReport.cardiacFlicker && ultrasoundReport.cardiacFlicker !== 'N/A' && (
                    <tr>
                      <th>Cardiac Flicker</th>
                      <td>{ultrasoundReport.cardiacFlicker}</td>
                    </tr>
                  )}
                  {ultrasoundReport.biparietalDiameter && (
                    <tr>
                      <th>Biparietal Diameter (BPD)</th>
                      <td>{ultrasoundReport.biparietalDiameter} mm</td>
                    </tr>
                  )}
                  {ultrasoundReport.headCircumference && (
                    <tr>
                      <th>Head Circumference (HC)</th>
                      <td>{ultrasoundReport.headCircumference} mm</td>
                    </tr>
                  )}
                  {ultrasoundReport.abdominalCircumference && (
                    <tr>
                      <th>Abdominal Circumference (AC)</th>
                      <td>{ultrasoundReport.abdominalCircumference} mm</td>
                    </tr>
                  )}
                  {ultrasoundReport.femurLength && (
                    <tr>
                      <th>Femur Length (FL)</th>
                      <td>{ultrasoundReport.femurLength} mm</td>
                    </tr>
                  )}
                </tbody>
              </Table>
              
              {/* Additional information in a separate table */}
              <Table bordered size="sm" className="mt-3">
                <tbody>
                  {ultrasoundReport.placentaPosition && ultrasoundReport.placentaPosition !== 'N/A' && (
                    <tr>
                      <th>Placenta Position</th>
                      <td>{ultrasoundReport.placentaPosition}</td>
                    </tr>
                  )}
                  {ultrasoundReport.amniotic && ultrasoundReport.amniotic !== 'N/A' && (
                    <tr>
                      <th>Amniotic Fluid</th>
                      <td>{ultrasoundReport.amniotic}</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          );
        } catch (err) {
          console.error("Error rendering obstetric measurements:", err);
          return <div className="text-danger">Error displaying measurements</div>;
        }
      };
      
      // Function to render abdominal organ findings
      const renderAbdominalFindings = () => {
        try {
          const hasAbdominalData = ultrasoundReport.liver || 
                                 ultrasoundReport.gallbladder || 
                                 ultrasoundReport.pancreas || 
                                 ultrasoundReport.spleen || 
                                 ultrasoundReport.kidneys || 
                                 ultrasoundReport.otherStructures;
          
          if (!hasAbdominalData) return null;
          
          return (
            <div className="ultrasound-tables">
              <h3>Organ-Specific Findings</h3>
              <Table bordered size="sm">
                <thead>
                  <tr>
                    <th>Organ</th>
                    <th>Finding</th>
                  </tr>
                </thead>
                <tbody>
                  {ultrasoundReport.liver && (
                    <tr>
                      <th>Liver</th>
                      <td>{ultrasoundReport.liver}</td>
                    </tr>
                  )}
                  {ultrasoundReport.gallbladder && (
                    <tr>
                      <th>Gallbladder</th>
                      <td>{ultrasoundReport.gallbladder}</td>
                    </tr>
                  )}
                  {ultrasoundReport.pancreas && (
                    <tr>
                      <th>Pancreas</th>
                      <td>{ultrasoundReport.pancreas}</td>
                    </tr>
                  )}
                  {ultrasoundReport.spleen && (
                    <tr>
                      <th>Spleen</th>
                      <td>{ultrasoundReport.spleen}</td>
                    </tr>
                  )}
                  {ultrasoundReport.kidneys && (
                    <tr>
                      <th>Kidneys</th>
                      <td>{ultrasoundReport.kidneys}</td>
                    </tr>
                  )}
                  {ultrasoundReport.otherStructures && (
                    <tr>
                      <th>Other Structures</th>
                      <td>{ultrasoundReport.otherStructures}</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          );
        } catch (err) {
          console.error("Error rendering abdominal findings:", err);
          return <div className="text-danger">Error displaying abdominal findings</div>;
        }
      };
      
      return (
        <div className="ultrasound-report" style={{ position: 'relative', minHeight: '97vh' }}>
          <div className="clinic-header">
            <div style={{ width: '80px', marginRight: '15px' }}>
              <img 
                src={clinic?.logo || "/logo.png"} 
                alt={clinic?.name || "Clinic Logo"} 
                style={{ maxWidth: '100%', height: 'auto' }} 
              />
            </div>
            <div>
              <h2 style={{ 
                margin: '0', 
                fontFamily: 'Georgia, serif', 
                color: '#1a5276', 
                fontWeight: 'bold',
                letterSpacing: '1px'
              }}>
                {clinic?.name || "Your Clinic Name"}
              </h2>
              <p style={{ margin: '2px 0 0 0', fontSize: '12px' }}>{clinic?.address || "Address"} | {clinic?.contactNumber || "Contact Information"}</p>
            </div>
          </div>
          
          <div className="report-content">
            <h3 className="text-center mt-3">Ultrasound Report</h3>
            <hr style={{ margin: '5px 0' }} />
            <p className="text-center">
              <strong>Patient:</strong> {safePatientData.name} | 
              <strong> ID:</strong> {safePatientData.patientId} | 
              <strong> Date:</strong> {report?.date ? new Date(report.date).toLocaleDateString() : 'Unknown'}
            </p>
            
            <div className="ultrasound-report-body">
              <div className="mb-3">
                <span className="field-label">Type of Ultrasound:</span>
                <span className="field-value">{ultrasoundReport?.type || 'Not specified'}</span>
              </div>
              
              {ultrasoundReport?.findings && (
                <div className="mb-3">
                  <h3>Findings</h3>
                  <p>{ultrasoundReport.findings}</p>
                </div>
              )}
              
              {ultrasoundReport?.type === 'Obstetric' && renderObstetricMeasurements()}
              
              {ultrasoundReport?.type === 'Abdominal' && renderAbdominalFindings()}
              
              {ultrasoundReport?.impression && (
                <div className="mb-3">
                  <h3>Impression</h3>
                  <p>{ultrasoundReport.impression}</p>
                </div>
              )}

              {/* Display ultrasound images if available */}
              {ultrasoundReport?.images && ultrasoundReport.images.length > 0 && (
                <div className="mb-3">
                  <h3>Ultrasound Images</h3>
                  <div className="ultrasound-images">
                    <table style={{ width: '100%', border: 'none', borderCollapse: 'collapse' }}>
                      <tbody>
                        {/* Process images in pairs */}
                        {Array.from({ length: Math.ceil(ultrasoundReport.images.length / 2) }).map((_, rowIndex) => (
                          <tr key={rowIndex} style={{ display: 'flex', width: '100%', flexDirection: 'row' }}>
                            {/* First image in pair */}
                            {ultrasoundReport.images[rowIndex * 2] && (
                              <td style={{ width: '50%', display: 'inline-block', padding: '5px', textAlign: 'center', verticalAlign: 'top', float: 'left' }}>
                                <img
                                  src={window.location.origin + ultrasoundReport.images[rowIndex * 2].path}
                                  alt={`Ultrasound image ${rowIndex * 2 + 1}`}
                                  className="ultrasound-report-image"
                                  data-width={ultrasoundReport.images[rowIndex * 2].width || 300}
                                  data-height={ultrasoundReport.images[rowIndex * 2].height || 200}
                                  style={{ 
                                    width: `${ultrasoundReport.images[rowIndex * 2].width || 300}px`,
                                    height: `${ultrasoundReport.images[rowIndex * 2].height || 200}px`,
                                    maxWidth: '100%',
                                    objectFit: 'contain',
                                    border: '1px solid #ddd',
                                    padding: '4px',
                                    backgroundColor: '#f8f9fa',
                                    display: 'inline-block',
                                    margin: '0 auto'
                                  }}
                                />
                                {ultrasoundReport.images[rowIndex * 2].caption && (
                                  <p className="ultrasound-image-caption">
                                    {ultrasoundReport.images[rowIndex * 2].caption}
                                  </p>
                                )}
                              </td>
                            )}
                            
                            {/* Second image in pair */}
                            {ultrasoundReport.images[rowIndex * 2 + 1] && (
                              <td style={{ width: '50%', display: 'inline-block', padding: '5px', textAlign: 'center', verticalAlign: 'top', float: 'left' }}>
                                <img
                                  src={window.location.origin + ultrasoundReport.images[rowIndex * 2 + 1].path}
                                  alt={`Ultrasound image ${rowIndex * 2 + 2}`}
                                  className="ultrasound-report-image"
                                  data-width={ultrasoundReport.images[rowIndex * 2 + 1].width || 300}
                                  data-height={ultrasoundReport.images[rowIndex * 2 + 1].height || 200}
                                  style={{ 
                                    width: `${ultrasoundReport.images[rowIndex * 2 + 1].width || 300}px`,
                                    height: `${ultrasoundReport.images[rowIndex * 2 + 1].height || 200}px`,
                                    maxWidth: '100%',
                                    objectFit: 'contain',
                                    border: '1px solid #ddd',
                                    padding: '4px',
                                    backgroundColor: '#f8f9fa',
                                    display: 'inline-block',
                                    margin: '0 auto'
                                  }}
                                />
                                {ultrasoundReport.images[rowIndex * 2 + 1].caption && (
                                  <p className="ultrasound-image-caption">
                                    {ultrasoundReport.images[rowIndex * 2 + 1].caption}
                                  </p>
                                )}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {ultrasoundReport?.recommendations && (
                <div className="mb-3">
                  <h3>Recommendations</h3>
                  <p>{ultrasoundReport.recommendations}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    } catch (error) {
      console.error("Error rendering ultrasound report:", error);
      return null;
    }
  };
  
  // Calculate pagination for medications
  const indexOfLastMedication = currentPage * medicationsPerPage;
  const indexOfFirstMedication = indexOfLastMedication - medicationsPerPage;
  const currentMedications = report?.medications && Array.isArray(report.medications) ? 
    report.medications.slice(indexOfFirstMedication, indexOfLastMedication) : 
    [];
  
  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  
  return (
    <div>
      <PrintStyles />
      <div className="d-flex justify-content-between align-items-center mb-4 no-print">
        <h2>Report Details</h2>
        <div>
        {!hideBack && !isPublicAccess && (
          <Button 
            variant="outline-secondary" 
            className="me-2"
            onClick={() => navigate(-1)}
          >
            <FaArrowLeft className="me-2" /> Back
          </Button>
        )}
          {!isPublicAccess && (user?.role === 'admin' || user?.permissions?.editReportAction) && (
            <Link to={`/reports/edit/${report.id}`} className="btn btn-warning me-2">
              <FaEdit className="me-2" /> Edit Report
            </Link>
          )}
          {!isPublicAccess && (user?.role === 'admin' || user?.permissions?.deleteReportAction) && (
            <Button 
              variant="danger" 
              onClick={() => setShowDeleteModal(true)}
              className="me-2"
            >
              <FaTrash className="me-2" /> Delete
            </Button>
          )}
          <Button 
            variant="primary" 
            onClick={handlePrint} 
            disabled={isPrinting}
          >
            <FaPrint className="me-2" /> {isPrinting ? 'Preparing...' : 'Print Report'}
          </Button>
          <Button 
            variant="success" 
            onClick={handlePrint}
            className="ms-2"
          >
            <FaDownload className="me-2" /> Download PDF
          </Button>
        </div>
      </div>
      
      {/* Show ultrasound badge if available */}
      {!isPublicAccess && report.ultrasoundReport && report.ultrasoundReport.hasUltrasound && (
        <div className="mb-3 no-print">
          <Badge bg="info" pill className="me-2">
            <FaXRay className="me-1" /> {report.ultrasoundReport.type} Ultrasound Report Included
          </Badge>
        </div>
      )}
      
      <div className="no-print mb-4">
        {!isPublicAccess && (
        <Card>
          <Card.Header className="bg-primary text-white">
            <h5 className="mb-0">Report Summary</h5>
          </Card.Header>
          <Card.Body>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={{ width: '40%', padding: '4px' }}>
                    <p style={{ marginBottom: '8px' }}>
                      <strong>Patient ID:</strong> <span className="patient-id">{safePatientData.patientId}</span>
                    </p>
                  </td>
                  <td style={{ width: '60%', padding: '4px', textAlign: 'right' }}>
                    <p style={{ marginBottom: '8px' }}>
                      <strong>Date:</strong> {formattedDate}
                    </p>
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '4px' }}>
                    <p style={{ marginBottom: '8px' }}>
                      <strong>Patient Name:</strong> {safePatientData.name}
                    </p>
                  </td>
                  <td style={{ padding: '4px', textAlign: 'right' }}>
                    <p style={{ marginBottom: '8px' }}>
                      <strong>Doctor:</strong> {safeDoctorData.name}
                    </p>
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '4px' }}>
                    <p style={{ marginBottom: '8px' }}>
                      <strong>Age/Gender:</strong> {safePatientData.age}/{safePatientData.gender}
                    </p>
                  </td>
                  <td style={{ padding: '4px', textAlign: 'right' }}>
                    {report.bloodPressure && report.bloodPressure.systolic && report.bloodPressure.diastolic && (
                      <p style={{ marginBottom: '8px' }}>
                        <strong>Blood Pressure:</strong> {report.bloodPressure.systolic}/{report.bloodPressure.diastolic} mmHg
                      </p>
                    )}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '4px' }}>
                    <p style={{ marginBottom: '8px' }}>
                      <strong>Blood Group:</strong> {safePatientData.bloodGroup && safePatientData.bloodGroup !== "" ? safePatientData.bloodGroup : "None"}
                    </p>
                  </td>
                  <td style={{ padding: '4px', textAlign: 'right' }}>
                    {report.followUpDate && (
                      <p style={{ marginBottom: '8px' }}>
                        <strong>Follow Up Date:</strong> {new Date(report.followUpDate).toLocaleDateString()}
                      </p>
                    )}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '4px' }}>                    {!isPublicAccess && safePatientData && safePatientData.id && user?.role !== 'patient' && (
                      <Link to={`/patients/edit/${safePatientData.id}`} className="btn btn-sm btn-warning mt-2">
                        <FaUserEdit className="me-1" /> Edit Patient
                      </Link>
                    )}
                  </td>
                  <td style={{ padding: '4px', textAlign: 'right' }}>
                    <p style={{ marginBottom: '8px' }}>
                      <strong>Time:</strong> {formattedTime}
                    </p>
                  </td>
                </tr>
              </tbody>
            </table>
          </Card.Body>
        </Card>
        )}
      </div>
      
      <div className="no-print mb-4">
        {!isPublicAccess && report.ultrasoundReport && report.ultrasoundReport.hasUltrasound && (
          <Card>
            <Card.Header className="bg-info text-white">
              <h5 className="mb-0"><FaXRay className="me-2" /> Ultrasound Report Summary</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <p><strong>Type:</strong> {report.ultrasoundReport.type}</p>
                  {report.ultrasoundReport.impression && (
                    <p><strong>Impression:</strong> {report.ultrasoundReport.impression}</p>
                  )}
                </Col>
                <Col md={6}>
                  {report.ultrasoundReport.recommendations && (
                    <p><strong>Recommendations:</strong> {report.ultrasoundReport.recommendations}</p>
                  )}
                  {report.ultrasoundReport.type === 'Obstetric' && report.ultrasoundReport.gestationalAge && (
                    <p><strong>Gestational Age:</strong> {report.ultrasoundReport.gestationalAge}</p>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}
      </div>
      
      {/* Printable report */}
      <div ref={reportRef} className="report-card p-4">
        {/* First page - regular medical report */}
        <div>
          <div className="clinic-header">
            <div style={{ width: '80px', marginRight: '15px' }}>
              <img 
                src={clinic?.logo || "/logo.png"} 
                alt={clinic?.name || "Clinic Logo"} 
                style={{ maxWidth: '100%', height: 'auto' }} 
              />
            </div>
            <div>
              <h2 style={{ 
                margin: '0', 
                fontFamily: 'Georgia, serif', 
                color: '#1a5276', 
                fontWeight: 'bold',
                letterSpacing: '1px'
              }}>
                {clinic?.name || "Your Clinic Name"}
              </h2>
              <p style={{ margin: '2px 0 0 0', fontSize: '12px' }}>{clinic?.address || "Address"} | {clinic?.contactNumber || "Contact Information"}</p>
            </div>
            <div style={{ marginLeft: 'auto', paddingLeft: '15px' }}>
              <QRCodeSVG 
                value={`${window.location.origin}/s/${btoa(`public-report/${report.id}`)}`} 
                size={80} 
                level="M" 
              />
            </div>
          </div>
          
          <div className="report-content">
            <div className="report-header">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr>
                    <td style={{ width: '50%', verticalAlign: 'top', paddingLeft: '0' }}>
                      <p style={{ marginBottom: '4px', textAlign: 'left' }}>
                        <strong>Patient ID:</strong> <span className="patient-id">{safePatientData.patientId}</span>
                      </p>
                    </td>
                    <td style={{ width: '50%', verticalAlign: 'top', textAlign: 'right', paddingRight: '0' }}>
                      <p style={{ marginBottom: '4px' }}>
                        <strong>Date:</strong> {formattedDate}
                      </p>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ verticalAlign: 'top', paddingLeft: '0' }}>
                      <p style={{ marginBottom: '4px', textAlign: 'left' }}>
                        <strong>Patient Name:</strong> {safePatientData.name}
                      </p>
                    </td>
                    <td style={{ verticalAlign: 'top', textAlign: 'right', paddingRight: '0' }}>
                      <p style={{ marginBottom: '4px' }}>
                        <strong>Doctor:</strong> {safeDoctorData.name}
                      </p>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ verticalAlign: 'top', paddingLeft: '0' }}>
                      <p style={{ marginBottom: '4px', textAlign: 'left' }}>
                        <strong>Age/Gender:</strong> {safePatientData.age}/{safePatientData.gender}
                      </p>
                    </td>
                    <td style={{ verticalAlign: 'top', textAlign: 'right', paddingRight: '0' }}>
                      {report.bloodPressure && report.bloodPressure.systolic && report.bloodPressure.diastolic && (
                        <p style={{ marginBottom: '4px' }}>
                          <strong>Blood Pressure:</strong> {report.bloodPressure.systolic}/{report.bloodPressure.diastolic} mmHg
                        </p>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ verticalAlign: 'top', paddingLeft: '0' }}>
                      <p style={{ marginBottom: '4px', textAlign: 'left' }}><strong>Blood Group:</strong> {safePatientData.bloodGroup && safePatientData.bloodGroup !== "" ? safePatientData.bloodGroup : "None"}</p>
                    </td>
                    <td style={{ verticalAlign: 'top', textAlign: 'right', paddingRight: '0' }}>
                      {report.followUpDate && (
                        <p style={{ marginBottom: '4px' }}><FaCalendarAlt className="me-1" /><strong>Follow Up:</strong> {new Date(report.followUpDate).toLocaleDateString()}</p>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div className="report-body">
              <div className="d-flex justify-content-between mb-3">
                <div>
                  <h5 className="mb-2">Diagnosis</h5>
                  <p className="mb-3">{report.diagnosis}</p>
                  
                  {report.doctorAdvice && (
                    <div className="mt-3">
                      <h5 className="mb-1">Doctor's Advice</h5>
                      <p>{report.doctorAdvice}</p>
                    </div>
                  )}
                </div>
              </div>
              
              <hr />
              
              <h5 className="text-center mb-3">Medications</h5>
              {report.medications && report.medications.length > 0 ? (
                <>
                  <Table className="medication-table">
                    <thead>
                      <tr>
                        <th>Medicine</th>
                        <th>Instructions</th>
                        <th>Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentMedications.map((med, index) => {
                        // Extract medicine name using best available method
                        let medicineName = "Unknown";
                        
                        // Try to get name from our enhanced fields first
                        if (med.medicineName) {
                          medicineName = med.medicineName;
                        }
                        // Then try from medicine_data (stored by server)
                        else if (med.medicine_data && med.medicine_data.name) {
                          medicineName = med.medicine_data.name;
                        }
                        // Then try from medicineDetails
                        else if (med.medicineDetails && med.medicineDetails.name) {
                          medicineName = med.medicineDetails.name;
                        }
                        // Then from the medicine object directly if it's an object
                        else if (med.medicine && typeof med.medicine === 'object' && med.medicine.name) {
                          medicineName = med.medicine.name;
                        }
                        // If medicine is just an ID as string, use that as last resort
                        else if (typeof med.medicine === 'string') {
                          medicineName = med.medicine;
                        }
                        
                        return (
                          <tr key={index}>
                            <td>
                              <strong>{medicineName}</strong>
                              {med.providedFromStock && (
                                <span className="ms-1">
                                  <small>({med.quantity} {med.medicineDetails?.unit || med.medicine?.unit || 'units'})</small>
                                  <FaCheck className="text-success ms-1" style={{fontSize: '12px'}} /> 
                                </span>
                              )}
                            </td>
                            <td>
                              <div className="bi-lingual">
                                <div>{formatMedicationInstructions(med)}</div>
                                <div className="urdu-text">{formatMedicationInstructionsUrdu(med)}</div>
                              </div>
                              {med.instructions && <div><small>Note: {med.instructions}</small></div>}
                            </td>
                            <td>{med.duration}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                  
                  {report.medications && report.medications.length > medicationsPerPage && (
                    <div className="d-flex flex-column align-items-center mt-3 mb-3 medication-pagination">
                      <div className="text-center mb-2 page-count">
                        <small>
                          Showing {indexOfFirstMedication + 1}-{Math.min(indexOfLastMedication, report.medications?.length || 0)} of {report.medications?.length || 0} medications
                        </small>
                      </div>
                      <Pagination>
                        <Pagination.Prev
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                        />
                        
                        {Array.from({ length: Math.ceil((report.medications?.length || 0) / medicationsPerPage) }).map((_, index) => (
                          <Pagination.Item
                            key={index + 1}
                            active={index + 1 === currentPage}
                            onClick={() => paginate(index + 1)}
                          >
                            {index + 1}
                          </Pagination.Item>
                        ))}
                        
                        <Pagination.Next
                          onClick={() => setCurrentPage(prev => 
                            Math.min(prev + 1, Math.ceil((report.medications?.length || 0) / medicationsPerPage))
                          )}
                          disabled={currentPage === Math.ceil((report.medications?.length || 0) / medicationsPerPage)}
                        />
                      </Pagination>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-center">No medications prescribed</p>
              )}
            </div>
            
            <div className="report-footer">
              <div className="footer-doctor-signature" style={{ display: 'flex', justifyContent: 'space-around', width: '100%' }}>
                {Array.from({ length: 3 }).map((_, index) => {
                  const doctor = signedDoctors[index] || (index === 0 ? safeDoctorData : null);
                  return (
                    <div key={index} style={{ width: '33.33%', textAlign: 'center' }}>
                      {doctor && (
                        <>
                          <p className="footer-doctor-name">Dr. {doctor.name}</p>
                          <p className="footer-doctor-qualification" style={{ whiteSpace: 'pre-line' }}>{doctor.qualification || 'MBBS'}</p>
                          <p className="footer-doctor-qualification" style={{ whiteSpace: 'pre-line' }}>{doctor.specialization || ''}</p>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
              
              <div className="footer-clinic-hours" style={{ marginBottom: '4px' }}>
                {clinic?.timings ? (
                  <>Clinic Hours: {formatTimeWithAMPM(clinic.timings.open)} - {formatTimeWithAMPM(clinic.timings.close)}
                  {clinic.workingDays && clinic.workingDays.length > 0 && 
                    ` | Working Days: ${clinic.workingDays.join(', ')}`
                  }</>
                ) : (
                  'Clinic Hours: 9:00 AM - 8:00 PM | Working Days: Monday, Tuesday, Wednesday, Thursday, Friday, Saturday'
                )}
              </div>
              
              <div className="footer-credit">
                Designed by Abdullah Arif | Contact: +92-3137323866 | Email: abdullaharif7323@gmail.com
              </div>
            </div>
          </div>
          
        </div>
        
        {/* PAGE 2: Ultrasound Report - SEPARATE PAGE */}
        {report.ultrasoundReport && report.ultrasoundReport.hasUltrasound && (
          <div className="ultrasound-report-page">
            {renderUltrasoundReport(report)}
            
            {/* Footer for ultrasound page */}
            <div className="report-footer">
              <div className="footer-doctor-signature" style={{ display: 'flex', justifyContent: 'space-around', width: '100%' }}>
                {Array.from({ length: 3 }).map((_, index) => {
                  const doctor = signedDoctors[index] || (index === 0 ? safeDoctorData : null);
                  return (
                    <div key={index} style={{ width: '33.33%', textAlign: 'center' }}>
                      {doctor && (
                        <>
                          <p className="footer-doctor-name">Dr. {doctor.name}</p>
                          <p className="footer-doctor-qualification" style={{ whiteSpace: 'pre-line' }}>{doctor.qualification || 'MBBS'}</p>
                          <p className="footer-doctor-qualification" style={{ whiteSpace: 'pre-line' }}>{doctor.specialization || ''}</p>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
              
              <div className="footer-clinic-hours" style={{ marginBottom: '4px' }}>
                {clinic?.timings ? (
                  <>Clinic Hours: {formatTimeWithAMPM(clinic.timings.open)} - {formatTimeWithAMPM(clinic.timings.close)}
                  {clinic.workingDays && clinic.workingDays.length > 0 && 
                    ` | Working Days: ${clinic.workingDays.join(', ')}`
                  }</>
                ) : (
                  'Clinic Hours: 9:00 AM - 8:00 PM | Working Days: Monday, Tuesday, Wednesday, Thursday, Friday, Saturday'
                )}
              </div>
              
              <div className="footer-credit">
                Designed by Abdullah Arif | Contact: +92-3137323866 | Email: abdullaharif7323@gmail.com
              </div>
            </div>
          </div>
        )}
      </div>
        
      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this report?
          <p>Patient: <strong>{safePatientData.name}</strong></p>
          <p>Date: <strong>{formattedDate}</strong></p>
          <p className="mt-2 text-danger">
            <strong>Warning:</strong> This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete Report
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ReportDetail; 

